/* GWP rule evaluation — pure logic, no DOM/network. Loaded before gwp.js.
   Also consumable by `node --test tests/gwp-rules.test.js`. */
(function (root) {
  'use strict';

  // A candidate gift line for THIS offer is a variant-id match. A truthy `_gwp` property
  // alone is not sufficient to identify "the" gift line (see evaluateOffer's selection
  // logic below, which prefers a `_gwp`-tagged match among candidates) — and a `_gwp`
  // property on a line whose variant no longer matches the current offer doesn't make it
  // a candidate at all: if the store edits the offer's gift variant mid-trial, the old
  // line still carries `_gwp` but is no longer discounted (full price). Matching it as
  // "the gift" would suppress adding the new gift and strand the customer with a paid
  // line forever. Old `_gwp` lines that don't match the current variant are "stale" and
  // reported separately so the caller can remove them.
  function isGiftLine(item, config) {
    return item.variant_id === config.giftVariantId;
  }

  function isStaleGiftLine(item, config) {
    return !!(item.properties && item.properties._gwp) && item.variant_id !== config.giftVariantId;
  }

  function evaluateOffer(config, cart, context) {
    context = context || {};
    // `trace` narrates every decision for debug logging (gwp.js prints it when the
    // offer's debug flag is on). Always returned; costs a few strings per evaluation.
    var trace = [];
    if (!config || !config.active || !config.giftVariantId) {
      trace.push('config missing, inactive, or has no giftVariantId → cleanup');
      return { action: 'cleanup', giftLine: null, staleLines: [], trace: trace };
    }

    var items = (cart && cart.items) || [];
    var bareMatches = [];
    var staleLines = [];
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (isGiftLine(it, config)) {
        bareMatches.push(it);
      } else if (isStaleGiftLine(it, config)) {
        staleLines.push(it);
      }
    }

    // Gift-line selection: prefer the line that is BOTH a variant match AND carries our own
    // `_gwp` property — that's the line every enforcement rule below must act on. A bare
    // variant-id match alone is not enough: a customer can manually add a paid unit of the
    // same product, and if that line happened to shadow the real `_gwp` line (e.g. by sitting
    // earlier in items[]), every mutation below (remove/clamp/free-ness) would wrongly target
    // the customer's paid line. Only when NO `_gwp`-tagged match exists do we fall back to the
    // first bare match, and only so the caller can suppress adding a second gift — that
    // fallback line is never removed or clamped (see the `_gwp` gates below).
    var giftLine = null;
    for (var j = 0; j < bareMatches.length; j++) {
      if (bareMatches[j].properties && bareMatches[j].properties._gwp) { giftLine = bareMatches[j]; break; }
    }
    if (!giftLine && bareMatches.length) giftLine = bareMatches[0];

    // Threshold basis: line-item subtotal after line-level discounts, excluding the gift
    // line AND all stale lines — the same basis SupaEasy evaluates, so both layers agree
    // at the boundary, and leftover gift lines from an edited/previous offer never count
    // toward re-qualifying for a new one. Only a genuine `_gwp` gift line is excluded — a
    // customer's own paid unit of the gift variant counts toward the subtotal like any other
    // line, matching the Liquid progress snippet's basis (snippets/gwp-progress.liquid).
    var giftIsOurs = !!(giftLine && giftLine.properties && giftLine.properties._gwp);
    var giftCents = giftIsOurs ? giftLine.final_line_price : 0;
    var staleCents = staleLines.reduce(function (sum, l) { return sum + l.final_line_price; }, 0);
    var subtotalExGift = ((cart && cart.items_subtotal_price) || 0) - giftCents - staleCents;

    if (giftLine) {
      trace.push('gift line: ' + (giftIsOurs ? 'ours (_gwp, key=' + giftLine.key + ', price=' + giftLine.final_line_price + ')' : 'customer paid line (no _gwp) — protected'));
    } else {
      trace.push('gift line: none in cart');
    }
    if (staleLines.length) trace.push('stale gift lines: ' + staleLines.length + ' (removed by manager)');
    trace.push('subtotal basis: ' + subtotalExGift + '¢ (items_subtotal ' + ((cart && cart.items_subtotal_price) || 0) + '¢ − gift ' + giftCents + '¢ − stale ' + staleCents + '¢)');

    var qualifies = true;
    if (config.newCustomersOnly) {
      if (typeof config.customerOrdersCount === 'number' && config.customerOrdersCount > 0) {
        qualifies = false;
        trace.push('newCustomersOnly: FAIL (ordersCount=' + config.customerOrdersCount + ')');
      } else {
        trace.push('newCustomersOnly: pass (' + (config.customerOrdersCount === null || config.customerOrdersCount === undefined ? 'guest — eligible' : 'ordersCount=0') + ')');
      }
    } else {
      trace.push('newCustomersOnly: rule off');
    }
    if (qualifies && config.requireOptIn) {
      if (!context.optedIn) {
        qualifies = false;
        trace.push('requireOptIn: FAIL (not opted in — no localStorage flag)');
      } else {
        trace.push('requireOptIn: pass');
      }
    } else if (qualifies) {
      trace.push('requireOptIn: rule off');
    }
    if (qualifies && Array.isArray(config.triggerProductIds) && config.triggerProductIds.length > 0) {
      var hasTrigger = items.some(function (item) {
        return !isGiftLine(item, config) && !isStaleGiftLine(item, config) &&
          config.triggerProductIds.indexOf(item.product_id) !== -1;
      });
      if (!hasTrigger) qualifies = false;
      trace.push('triggerProducts: ' + (hasTrigger ? 'pass' : 'FAIL (none of [' + config.triggerProductIds.join(',') + '] in cart)'));
    } else if (qualifies) {
      trace.push('triggerProducts: rule off');
    }
    if (qualifies && typeof config.thresholdCents === 'number' && config.thresholdCents > 0) {
      if (subtotalExGift < config.thresholdCents) {
        qualifies = false;
        trace.push('threshold: FAIL (' + subtotalExGift + '¢ < ' + config.thresholdCents + '¢)');
      } else {
        trace.push('threshold: pass (' + subtotalExGift + '¢ ≥ ' + config.thresholdCents + '¢)');
      }
    } else if (qualifies) {
      trace.push('threshold: rule off');
    }

    if (qualifies && !giftLine) {
      trace.push('action: add (qualified, no gift in cart)');
      return { action: 'add', giftLine: null, staleLines: staleLines, trace: trace };
    }
    // A paid, manually-added line (no `_gwp`) must never be deleted just because the cart no
    // longer qualifies for the offer — only a line the theme itself added is fair game.
    if (!qualifies && giftLine) {
      if (giftIsOurs) {
        trace.push('action: remove (no longer qualified, gift is ours)');
        return { action: 'remove', giftLine: giftLine, staleLines: staleLines, trace: trace };
      }
      trace.push('action: none (not qualified; only a customer paid line present — never touched)');
      return { action: 'none', giftLine: giftLine, staleLines: staleLines, trace: trace };
    }
    // Continuous free-ness enforcement: a line we added as a free gift (carries our own
    // `_gwp` property) must actually be free. If SupaEasy's discount is paused/edited while
    // the metaobject offer stays Active, this line can end up with a nonzero
    // final_line_price — the cart would otherwise silently charge the customer for what the
    // theme still displays as a "free" gift, forever, since nothing else re-checks it after
    // add time. Remove it rather than let that happen; gwp.js will re-add a fresh $0 gift
    // line once enforcement resumes and the cart re-qualifies. This check is gated on `_gwp`
    // specifically (not just a variant-id match) so a customer's own manually-added, paid
    // unit of the same product is never touched by this rule.
    if (qualifies && giftIsOurs && giftLine.final_line_price > 0) {
      trace.push('action: remove (free-ness violation — our gift line priced at ' + giftLine.final_line_price + '¢, expected 0; is the SupaEasy discount active and qualifying?)');
      return { action: 'remove', giftLine: giftLine, staleLines: staleLines, trace: trace };
    }
    // Quantity clamp is likewise gated on `_gwp` — a customer who buys 2 paid units of the
    // gift-eligible product must never have their quantity silently clamped to 1.
    if (qualifies && giftIsOurs && giftLine.quantity > 1) {
      trace.push('action: clamp (gift quantity ' + giftLine.quantity + ' → 1)');
      return { action: 'clamp', giftLine: giftLine, staleLines: staleLines, trace: trace };
    }
    trace.push('action: none (' + (qualifies ? 'qualified, gift already correct' : 'not qualified, nothing to undo') + ')');
    return { action: 'none', giftLine: giftLine, staleLines: staleLines, trace: trace };
  }

  var api = { evaluateOffer: evaluateOffer };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.GWPRules = api;
})(typeof window !== 'undefined' ? window : globalThis);
